import React, { useRef, useState, useEffect } from 'react';
import MapFrame from '../../components/MapFrame';
import RSVPForm       from '../../components/RSVPForm';
import Wishlist       from '../../components/Wishlist';
import YandexMap      from '../../components/YandexMap';
import Countdown      from '../../components/Countdown';
import TickerBar      from '../../components/TickerBar';
import FixedCTA       from '../../components/FixedCTA';
import useReveal      from '../../components/useReveal';
import Schedule       from '../../components/Schedule';
import DresscodePalette from '../../components/DresscodePalette';
import CalendarButton from '../../components/CalendarButton';
import BlockSubtitle  from '../../components/BlockSubtitle';
import PaidWatermark from '../../components/PaidWatermark';
import MusicPlayer from '../../components/MusicPlayer';
import './Organica.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const src = u => !u ? null : (u.startsWith('http') ? u : u);
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

/* Load Viaoda Libre + Playfair + Montserrat once. */
function useOrganicaFonts() {
  useEffect(() => {
    const id = 'org-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Viaoda+Libre&family=Playfair:ital,opsz,wght@0,5..1200,400..900;1,5..1200,400..900&family=Montserrat:wght@300;400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);
}

/* Названия месяцев: именительный — для заголовка календаря («Сентябрь»),
   родительный — для строки даты «13 сентября 2026 ГОДА». */
const MONTHS_RU_NOM = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const MONTHS_RU_GEN = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
const DAYS_SHORT    = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

/* Build a simple month grid for the given Date. */
function buildMonth(d) {
  const year = d.getFullYear();
  const month = d.getMonth();
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  /* weekday Mon-based: 0=Mon..6=Sun */
  const offset = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let day = 1; day <= last.getDate(); day++) cells.push(day);
  return { monthName: MONTHS_RU_NOM[month], cells, activeDay: d.getDate() };
}

const fmtDateDots = raw => {
  if (!raw) return '??.??.????';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join('.');
};

/* ── Class maps ──────────────────────────────────────────────── */
const ORG_TICKER_CLASSES = { bar: 'org-ticker', track: 'org-ticker-track', item: 'org-ticker-item', sep: 'org-ticker-sep' };
const ORG_CD_CLASSES     = { root: 'org-cd',     cell: 'org-cd-cell', num: 'org-cd-num', label: 'org-cd-lbl' };
const ORG_SCHED_CLASSES  = { root: 'org-sched',  row: 'org-sched-row', rowBorder: 'org-sched-row--border', time: 'org-sched-time', info: 'org-sched-info', name: 'org-sched-name', desc: 'org-sched-desc' };
const ORG_DRESS_CLASSES  = { wrap: 'org-swatches', swatchWrap: 'org-swatch-wrap', swatch: 'org-swatch', swatchLabel: 'org-swatch-lbl', photos: 'org-dc-photos', photo: 'org-dc-photo' };
const ORG_RSVP_CLASSES = {
  form:    'org-form',
  tabs:    'org-rsvp-tabs',
  tab:     'org-rsvp-tab',
  tabSel:  'org-rsvp-tab--sel',
  field:   'org-form-field',
  label:   'org-form-lbl',
  input:   'org-form-input',
  submit:  'org-btn org-btn--primary org-btn--full',
  success: 'org-form-ok',
  error:   'org-form-err',
  chip:    'org-chip',
  chipSel: 'org-chip--sel',
  chips:   'org-form-chips',
};
const ORG_WISHLIST_CLASSES = {
  list:        'org-wish',
  item:        'org-wish-item',
  itemBorder:  'org-wish-item--border',
  row:         'org-wish-row',
  name:        'org-wish-name',
  price:       'org-wish-price',
  btn:         'org-btn org-btn--outline org-btn--full',
  btnReserved: 'org-btn--res',
};
const ORG_WISHLIST_MODAL_CLASSES = {
  bg:         'org-wish-modal-bg',
  modal:      'org-wish-modal',
  title:      'org-wish-modal-title',
  subtitle:   'org-wish-modal-sub',
  input:      'org-form-input',
  error:      'org-form-err',
  btns:       'org-wish-modal-btns',
  btnCancel:  'org-btn org-btn--outline',
  btnConfirm: 'org-btn org-btn--primary',
};

/* Wavy separator — three repetitions of the SVG spanning the width.
   Uses CSS mask so the fill follows the current accent palette colour
   instead of the SVG's hard-coded green. */
function OrgWave({ flip = false }) {
  const tileStyle = {
    WebkitMaskImage: "url('/templates/organica/border.svg')",
    maskImage:       "url('/templates/organica/border.svg')",
  };
  return (
    <div className={`org-wave${flip ? ' org-wave--flip' : ''}`} aria-hidden="true">
      <span className="org-wave-tile" style={tileStyle} />
      <span className="org-wave-tile" style={tileStyle} />
      <span className="org-wave-tile" style={tileStyle} />
    </div>
  );
}

/* Инлайн декоративного SVG с перекраской в палитру (через <img>
   CSS-переменные не доходят — подменяем hex в разметке). */
const _orgSvgCache = {};
function OrgDeco({ src, className, colors }) {
  const [raw, setRaw] = useState(() => (_orgSvgCache[src] != null ? _orgSvgCache[src] : null));
  useEffect(() => {
    if (_orgSvgCache[src] != null) { setRaw(_orgSvgCache[src]); return; }
    let alive = true;
    fetch(src).then(r => (r.ok ? r.text() : '')).then(t => { _orgSvgCache[src] = t; if (alive) setRaw(t); }).catch(() => {});
    return () => { alive = false; };
  }, [src]);
  const cls = `org-svg${className ? ' ' + className : ''}`;
  if (!raw) return <span className={cls} aria-hidden="true" />;
  let html = raw;
  (colors || []).forEach(([from, to]) => { if (to && from !== to) html = html.split(from).join(to); });
  return <span className={cls} aria-hidden="true" dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ══ Main Template ═════════════════════════════════════════════ */
export default function Organica({ project, design, isPreview }) {
  const pageRef  = useRef(null);
  const blockDep = project?.blocks?.length;
  useReveal(pageRef, blockDep, { revealSelector: '.org-reveal', inClass: 'org-in' });
  useOrganicaFonts();

  const [inPreview, setInPreview] = useState(!!isPreview);
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    setInPreview(!!isPreview || !!root.closest('.lp-phone-screen,.lp-template-inner,[class*="lp-"]'));
  }, [isPreview]);

  const p = project || {};
  const d = design  || {};

  /* Colours — signature green on cream by default; user palette overrides
     via project.palette. Заголовки, кнопки и вторичные элементы следуют
     за ОСНОВНЫМ цветом (primary); secondary → фон. */
  const pal     = p.palette || {};
  const accent  = pal.primary   || '#2B3C2C';
  const pinColors = [['#16320D', accent]]; // пин локации следует за палитрой
  const bg      = pal.secondary || '#FDFBEF';
  const fg      = pal.primary    || '#2B3C2C';
  const onDark  = pal.textLight || '#FFFFFF';
  // touch d to silence unused var lint
  void d;

  /* Blocks */
  const blocks = p.blocks || ['menu','map','schedule','palette','rsvp','survey','message','wishlist','countdown','calendar'];
  const HAS_MENU     = blocks.includes('menu');
  const HAS_CALENDAR = blocks.includes('calendar');

  /* Text */
  const bride       = p.bride || '';
  const groom       = p.groom || '';
  const tagline     = p.block_content?.header?.subtitle || '';
  const footerQuote = p.block_content?.footer?.quote    || '';
  const invText     = p.invitation_text || p.block_content?.message?.text || '';
  const invTitle    = p.block_content?.message?.title || 'Дорогие наши!';

  /* Photos */
  const photos      = p.cover_photos || (p.cover_photo ? [{ url: p.cover_photo }] : []);
  const heroPhoto   = src(photos[0]?.url) || null;
  const heroPhotoMeta = photos[0] || null;
  const dcPhotos    = p.dresscode_photos || [];
  /* Футер-фото независимо от главного: если не задано — футер без фото. */
  const footerPhotoObj = p.footer_photo?.url ? p.footer_photo : null;
  const footerPhoto    = src(footerPhotoObj?.url) || null;

  /* Date — ticker shows uppercase "D MONTH YYYY ГОДА". */
  const weddingDate = p.wedding_date ? new Date(p.wedding_date) : null;
  const headerDate  = weddingDate
    ? `${weddingDate.getDate()} ${MONTHS_RU_GEN[weddingDate.getMonth()]} ${weddingDate.getFullYear()} ГОДА`.toUpperCase()
    : '';
  const dateDots    = fmtDateDots(p.wedding_date);
  const monthGrid   = weddingDate ? buildMonth(weddingDate) : null;

  /* Schedule */
  const schedule = p.schedule?.length ? p.schedule : [];

  /* Venues */
  const venues  = p.venues || [];

  /* Dresscode */
  const swatches = p.dresscode_palette || [];

  /* Wishlist */
  const wishlist = p.wishlist || [];

  /* Block content */
  const bc = p.block_content || {};
  const titleSchedule = bc.schedule?.title  || '';
  const titleLocation = bc.map?.title       || '';
  const titleDress    = bc.palette?.title   || '';
  const titleRsvp     = bc.rsvp?.title      || '';
  const titleWish     = bc.wishlist?.title  || '';
  const titleCD       = bc.countdown?.title || '';
  const calBtn        = bc.calendar?.title  || '';

  return (
    <div className={`org-page${inPreview ? ' org-page--preview' : ''}`} ref={pageRef}
      style={{ '--org-accent': accent, '--org-bg': bg, '--org-fg': fg, '--org-on-dark': onDark }}>
      <PaidWatermark isPaid={project?.is_paid} isPreview={inPreview} />

      {/* Navigation (top, on cream) */}
      {HAS_MENU && (
        <nav className="org-nav">
          {blocks.includes('schedule') && <a href="#org-schedule">{bc.menu?.schedule || 'Программа'}</a>}
          {blocks.includes('map')      && <a href="#org-location">{bc.menu?.location || 'Локация'}</a>}
          {blocks.includes('palette')  && <a href="#org-dresscode">{bc.menu?.dresscode || 'Дресс-код'}</a>}
          {blocks.includes('rsvp')     && <a href="#org-rsvp">{bc.menu?.rsvp || 'Анкета'}</a>}
        </nav>
      )}

      {/* Hero — date / names / tagline / photo */}
      <section className="org-hero org-reveal" data-nophoto={heroPhoto ? undefined : '1'}>
        {headerDate && <p className="org-hero-date">{headerDate}</p>}
        <div className="org-hero-names">
          {bride && <span className="org-hero-name">{bride}</span>}
          <span className="org-hero-amp">{bc.header?.amp || '&'}</span>
          {groom && <span className="org-hero-name">{groom}</span>}
        </div>
        {tagline && <p className="org-hero-tagline">{tagline}</p>}
        {heroPhoto && (
          <div className="org-hero-photo">
            <img src={heroPhoto} alt="" style={{ filter: flt(heroPhotoMeta), objectPosition: objPos(heroPhotoMeta) }} />
          </div>
        )}
      </section>

      {/* Invitation — on dark green, wavy borders above and below */}
      <section className="org-invite-sect org-reveal">
        <OrgWave />
        <div className="org-invite">
          <h2 className="org-invite-title">{invTitle}</h2>
          <div className="org-invite-body">
            {invText
              ? invText.split('\n').map((line, i) =>
                  line.trim() ? <p key={i}>{line}</p> : <br key={i} />
                )
              : null
            }
          </div>
        </div>

        {/* Calendar (dark green) */}
        {monthGrid && (
          <div className="org-cal">
            <p className="org-cal-month">{monthGrid.monthName}</p>
            <div className="org-cal-grid">
              {DAYS_SHORT.map(d2 => (
                <span key={d2} className="org-cal-dow">{d2}</span>
              ))}
              {monthGrid.cells.map((d2, i) => (
                <span key={i}
                  className={`org-cal-day${d2 === monthGrid.activeDay ? ' org-cal-day--on' : ''}${d2 == null ? ' org-cal-day--empty' : ''}`}>
                  {d2 ?? ''}
                </span>
              ))}
            </div>
            {HAS_CALENDAR && (
              <CalendarButton
                bride={bride}
                groom={groom}
                date={p.wedding_date}
                time={p.wedding_time}
                venues={p.venues}
                address={p.wedding_address}
                className="org-btn org-btn--outline-white">
                {calBtn || 'Добавить в календарь'}
              </CalendarButton>
            )}
          </div>
        )}

        <OrgWave flip />

        {/* Decorative foliage — Figma image 12 */}
        <div className="org-foliage org-foliage--invite" aria-hidden="true">
          <img className="org-leaf org-leaf--invite" src="/templates/organica/flower-invite.png" alt="" />
        </div>
      </section>

      {/* Dynamic blocks */}
      {['map','schedule','palette','rsvp','wishlist','countdown']
        .filter(b => blocks.includes(b))
        .map(blockId => {
          switch (blockId) {
            case 'map':
              return (
                <section key="map" className="org-map-sect org-reveal" id="org-location">
                  <h2 className="org-h">{titleLocation || 'Локация'}</h2>
                  <BlockSubtitle text={bc.map?.subtitle} className="org-p" />
                  {venues.map((v, i) => (
                    <div key={i} className="org-addr">
                      {(v.address || '').trim() && <OrgDeco className="org-addr-icon" src="/templates/organica/pin.svg" colors={pinColors} />}
                      <span className="org-addr-text">
                        {v.name && v.address ? `${v.name}, ${v.address}` : (v.name || v.address)}
                      </span>
                    </div>
                  ))}
                  {!venues.some(v => v.address) && p.wedding_address && (
                    <div className="org-addr">
                      <OrgDeco className="org-addr-icon" src="/templates/organica/pin.svg" colors={pinColors} />
                      <span className="org-addr-text">{p.wedding_address}</span>
                    </div>
                  )}
                  {/* Карта — только при наличии адреса (имя без адреса → без карты). */}
                  {(p.map_url || (p.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
                  <div className="org-map-wrap">
                    {(() => {
                      if (p.map_url) {
                        return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={p.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                      }
                      const withCoords = venues.filter(v => Number.isFinite(+v.lat) && Number.isFinite(+v.lon));
                      if (withCoords.length > 0) {
                        return <YandexMap points={withCoords} singleZoom={16} region={p.region} provider={bc.map?.provider} preset="islands#greenIcon" />;
                      }
                      const addr = venues.length > 0
                        ? venues.map(v => v.address).filter(Boolean).join(' — ')
                        : (p.wedding_address || '');
                      if (!addr) return null;
                      const s = `https://yandex.ru/map-widget/v1/?mode=search&text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                      return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={s} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                    })()}
                  </div>
                  )}
                </section>
              );

            case 'schedule':
              return (
                <section key="schedule" className="org-sched-sect org-reveal" id="org-schedule">
                  <OrgWave />
                  <div className="org-sched-inner">
                    <h2 className="org-h org-h--on-dark">{titleSchedule || 'Программа'}</h2>
                    <BlockSubtitle text={bc.schedule?.subtitle} className="org-p org-p--on-dark" />
                    <Schedule items={schedule} classes={ORG_SCHED_CLASSES} />
                  </div>
                  <OrgWave flip />
                  <div className="org-foliage org-foliage--sched" aria-hidden="true">
                    <img className="org-leaf org-leaf--sched" src="/templates/organica/flower-sched.png" alt="" />
                  </div>
                </section>
              );

            case 'palette':
              return (
                <section key="palette" className="org-dress-sect org-reveal" id="org-dresscode">
                  <h2 className="org-h">{titleDress || 'Дресс-код'}</h2>
                  <BlockSubtitle text={bc.palette?.subtitle} className="org-p" />
                  <DresscodePalette
                    swatches={swatches}
                    photos={dcPhotos}
                    cssFilter={flt}
                    classes={ORG_DRESS_CLASSES}
                  />
                </section>
              );

            case 'rsvp':
              return (
                <section key="rsvp" className="org-rsvp-sect org-reveal" id="org-rsvp">
                  <h2 className="org-h">{titleRsvp || 'Подтвердите присутствие'}</h2>
                  <BlockSubtitle text={bc.rsvp?.subtitle} className="org-p" />
                  <RSVPForm project={p} blocks={blocks} classes={ORG_RSVP_CLASSES} />
                </section>
              );

            case 'wishlist':
              return (
                <section key="wishlist" className="org-wish-sect org-reveal">
                  <OrgWave />
                  <div className="org-wish-inner">
                    <h2 className="org-h org-h--on-dark">{titleWish || 'Что дарить'}</h2>
                    <BlockSubtitle text={bc.wishlist?.subtitle} className="org-p org-p--on-dark" />
                    <Wishlist
                      items={wishlist} currency={bc.wishlist?.currency} reserveText={bc.wishlist?.reserveText}
                      slug={p.slug}
                      classes={ORG_WISHLIST_CLASSES}
                      modalClasses={ORG_WISHLIST_MODAL_CLASSES}
                    />
                  </div>
                  <OrgWave flip />
                  <div className="org-foliage org-foliage--wish" aria-hidden="true">
                    <img className="org-leaf org-leaf--wish" src="/templates/organica/flower-wish.png" alt="" />
                  </div>
                </section>
              );

            case 'countdown':
              return (
                <section key="countdown" className="org-cd-sect org-reveal">
                  <h2 className="org-h">{titleCD || 'До нашей свадьбы'}</h2>
                  <BlockSubtitle text={bc.countdown?.subtitle} className="org-p" />
                  <Countdown date={p.wedding_date} time={p.wedding_time} bc={bc} classes={ORG_CD_CLASSES}
                    separator=":" />
                </section>
              );

            default:
              return null;
          }
        })}

      {/* Footer — quote (dark green) + photo */}
      <footer className="org-footer org-reveal">
        <OrgWave />
        <div className="org-foliage org-foliage--footer" aria-hidden="true">
          <img className="org-leaf org-leaf--footer-a" src="/templates/organica/flower-footer-a.png" alt="" />
          <img className="org-leaf org-leaf--footer-b" src="/templates/organica/flower-footer-b.png" alt="" />
        </div>
        <div className="org-footer-quote">
          <p>{(footerQuote || 'Одна любовь,\nдругой такой\nи не было').split('\n').map((line, i, arr) => (
            <React.Fragment key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}</p>
        </div>
        {footerPhoto ? (
          <div className="org-footer-photo">
            <img src={footerPhoto} alt="" style={{ filter: flt(footerPhotoObj), objectPosition: objPos(footerPhotoObj) }} />
          </div>
        ) : null}
      </footer>

      {/* Fixed RSVP CTA */}
      {blocks.includes('rsvp') && (
        <FixedCTA
          href="#org-rsvp"
          targetId="org-rsvp"
          scrollRoot={pageRef}
          className="org-fixed-cta">
          {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
        </FixedCTA>
      )}

      {/* Hidden ticker bar — supports optional use later */}
      {false && <TickerBar text={dateDots} classes={ORG_TICKER_CLASSES} />}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={inPreview} />
      )}
    </div>
  );
}
